import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Download,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText
} from 'lucide-react';
import { eligibilityDataApi } from '../../services/api';
import QueryInfoButton from '../../components/QueryInfoButton';

const EligibilityData = () => {
  const [filters, setFilters] = useState({
    memberId: '',
    memNbr: '',
    subscriberMemNbr: '',
    firstName: '',
    lastName: '',
    email: '',
    partnerCode: '',
    planId: '',
    regionCode: '',
    homeState: '',
    eligibilityStartFrom: '',
    eligibilityStartTo: '',
    eligibilityEndFrom: '',
    eligibilityEndTo: '',
    publishStatus: '',
    limit: 50,
    offset: 0
  });

  const [showFilters, setShowFilters] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTriggered, setSearchTriggered] = useState(false);

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ['eligibilityDataFilters'],
    queryFn: async () => {
      const response = await eligibilityDataApi.getFilterOptions();
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Search query - only runs when searchTriggered is true
  const {
    data: searchResults,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['eligibilityDataSearch', filters],
    queryFn: async () => {
      const response = await eligibilityDataApi.search(filters);
      // axios interceptor already unwraps response.data, so response IS { success, data }
      return response.data;
    },
    enabled: searchTriggered,
    keepPreviousData: true
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, offset: 0 }));
  };

  const handleSearch = () => {
    setSearchTriggered(true);
    refetch();
  };

  const handleClearFilters = () => {
    setFilters({
      memberId: '',
      memNbr: '',
      subscriberMemNbr: '',
      firstName: '',
      lastName: '',
      email: '',
      partnerCode: '',
      planId: '',
      regionCode: '',
      homeState: '',
      eligibilityStartFrom: '',
      eligibilityStartTo: '',
      eligibilityEndFrom: '',
      eligibilityEndTo: '',
      publishStatus: '',
      limit: 50,
      offset: 0
    });
    setSearchTriggered(false);
  };

  const handlePageChange = (newOffset) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  useEffect(() => {
    if (searchTriggered && filters.offset > 0) {
      refetch();
    }
  }, [filters.offset]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = searchResults ? Math.ceil(searchResults.total / filters.limit) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Eligibility Data</h1>
          <p className="text-sm text-gray-500 mt-1">
            Search and view member import file data
          </p>
        </div>
        <div className="flex items-center gap-3">
          {searchResults?.query && (
            <QueryInfoButton
              title="Eligibility Data Query"
              queries={[{
                name: 'Member Import File Data Search',
                description: 'Query executed against etl_staging.etl_outbound.member_import_file_data table',
                query: searchResults.query,
                endpoint: '/api/eligibility-data/search'
              }]}
            />
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Member ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member ID</label>
              <input
                type="text"
                value={filters.memberId}
                onChange={(e) => handleFilterChange('memberId', e.target.value)}
                placeholder="Search member ID..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Mem Nbr */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mem Nbr</label>
              <input
                type="text"
                value={filters.memNbr}
                onChange={(e) => handleFilterChange('memNbr', e.target.value)}
                placeholder="Search mem nbr..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Subscriber Mem Nbr */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subscriber Mem Nbr</label>
              <input
                type="text"
                value={filters.subscriberMemNbr}
                onChange={(e) => handleFilterChange('subscriberMemNbr', e.target.value)}
                placeholder="Search subscriber..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={filters.firstName}
                onChange={(e) => handleFilterChange('firstName', e.target.value)}
                placeholder="Search first name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={filters.lastName}
                onChange={(e) => handleFilterChange('lastName', e.target.value)}
                placeholder="Search last name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                placeholder="Search email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Partner Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partner Code</label>
              <select
                value={filters.partnerCode}
                onChange={(e) => handleFilterChange('partnerCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Partners</option>
                {filterOptions?.partnerCodes?.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>

            {/* Plan ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan ID</label>
              <input
                type="text"
                value={filters.planId}
                onChange={(e) => handleFilterChange('planId', e.target.value)}
                placeholder="Search plan ID..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Region Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region Code</label>
              <select
                value={filters.regionCode}
                onChange={(e) => handleFilterChange('regionCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Regions</option>
                {filterOptions?.regionCodes?.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>

            {/* Home State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Home State</label>
              <select
                value={filters.homeState}
                onChange={(e) => handleFilterChange('homeState', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All States</option>
                {filterOptions?.states?.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Publish Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publish Status</label>
              <select
                value={filters.publishStatus}
                onChange={(e) => handleFilterChange('publishStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                {filterOptions?.publishStatuses?.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Eligibility Start From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility Start From</label>
              <input
                type="date"
                value={filters.eligibilityStartFrom}
                onChange={(e) => handleFilterChange('eligibilityStartFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Eligibility Start To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility Start To</label>
              <input
                type="date"
                value={filters.eligibilityStartTo}
                onChange={(e) => handleFilterChange('eligibilityStartTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Eligibility End From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility End From</label>
              <input
                type="date"
                value={filters.eligibilityEndFrom}
                onChange={(e) => handleFilterChange('eligibilityEndFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Eligibility End To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility End To</label>
              <input
                type="date"
                value={filters.eligibilityEndTo}
                onChange={(e) => handleFilterChange('eligibilityEndTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleSearch}
              disabled={isLoading || isFetching}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(isLoading || isFetching) ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {searchTriggered && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Results Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {searchResults ? (
                <>
                  Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, searchResults.total)} of{' '}
                  <span className="font-semibold">{searchResults.total.toLocaleString()}</span> records
                </>
              ) : (
                'Loading...'
              )}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Member Info</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mem Nbr</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan / Partner</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Eligibility</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading...
                    </td>
                  </tr>
                ) : searchResults?.records?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      No records found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  searchResults?.records?.map((record) => (
                    <tr key={record.member_import_file_data_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {[record.first_name, record.middle_name, record.last_name].filter(Boolean).join(' ') || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {record.member_id || '-'}
                        </div>
                        <div className="text-xs text-gray-400">
                          DOB: {formatDate(record.dob)} | {record.gender || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{record.mem_nbr || '-'}</div>
                        <div className="text-xs text-gray-500">
                          Sub: {record.subscriber_mem_nbr || '-'}
                        </div>
                        <div className="text-xs text-gray-400">
                          Type: {record.member_type || '-'} | {record.emp_or_dep || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{record.plan_id || '-'}</div>
                        <div className="text-xs text-gray-500">{record.partner_code || '-'}</div>
                        <div className="text-xs text-gray-400">
                          Region: {record.region_code || '-'} | Group: {record.group_id || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {formatDate(record.eligibility_start)}
                        </div>
                        <div className="text-xs text-gray-500">
                          to {formatDate(record.eligibility_end)}
                        </div>
                        <div className="text-xs text-gray-400">
                          Plan Type: {record.plan_type || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 truncate max-w-[200px]" title={record.email}>
                          {record.email || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.mobile_phone || record.home_phone_number || '-'}
                        </div>
                        <div className="text-xs text-gray-400 truncate max-w-[200px]">
                          {[record.home_city, record.home_state].filter(Boolean).join(', ') || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          record.publish_status === 'PUBLISHED' ? 'bg-green-100 text-green-800' :
                          record.publish_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          record.publish_status === 'FAILED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {record.publish_status || 'N/A'}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDateTime(record.create_ts)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedRecord(record)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {searchResults && searchResults.total > filters.limit && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(0, filters.offset - filters.limit))}
                  disabled={filters.offset === 0}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(filters.offset + filters.limit)}
                  disabled={!searchResults.hasMore}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!searchTriggered && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Search Eligibility Data</h3>
          <p className="text-gray-500 mb-4">
            Use the filters above to search for member import records
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Record Details</h2>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Member Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Member Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium">{[selectedRecord.first_name, selectedRecord.middle_name, selectedRecord.last_name].filter(Boolean).join(' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Member ID:</span>
                      <span className="font-medium">{selectedRecord.member_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mem Nbr:</span>
                      <span className="font-medium">{selectedRecord.mem_nbr}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subscriber Mem Nbr:</span>
                      <span className="font-medium">{selectedRecord.subscriber_mem_nbr}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">DOB:</span>
                      <span className="font-medium">{formatDate(selectedRecord.dob)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Age:</span>
                      <span className="font-medium">{selectedRecord.age}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gender:</span>
                      <span className="font-medium">{selectedRecord.gender}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Member Type:</span>
                      <span className="font-medium">{selectedRecord.member_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Emp/Dep:</span>
                      <span className="font-medium">{selectedRecord.emp_or_dep}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">SSO User:</span>
                      <span className="font-medium">{selectedRecord.is_sso_user ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                {/* Plan Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Plan Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Partner Code:</span>
                      <span className="font-medium">{selectedRecord.partner_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Region Code:</span>
                      <span className="font-medium">{selectedRecord.region_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan ID:</span>
                      <span className="font-medium">{selectedRecord.plan_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan Type:</span>
                      <span className="font-medium">{selectedRecord.plan_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subgroup ID:</span>
                      <span className="font-medium">{selectedRecord.subgroup_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Group ID:</span>
                      <span className="font-medium">{selectedRecord.group_id}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email:</span>
                      <span className="font-medium break-all">{selectedRecord.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile Phone:</span>
                      <span className="font-medium">{selectedRecord.mobile_phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Home Phone:</span>
                      <span className="font-medium">{selectedRecord.home_phone_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Language:</span>
                      <span className="font-medium">{selectedRecord.language_code}</span>
                    </div>
                  </div>
                </div>

                {/* Eligibility */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Eligibility
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Start Date:</span>
                      <span className="font-medium">{formatDate(selectedRecord.eligibility_start)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">End Date:</span>
                      <span className="font-medium">{formatDate(selectedRecord.eligibility_end)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Action:</span>
                      <span className="font-medium">{selectedRecord.action}</span>
                    </div>
                  </div>
                </div>

                {/* Home Address */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Home Address
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address Line 1:</span>
                      <span className="font-medium">{selectedRecord.home_address_line1}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address Line 2:</span>
                      <span className="font-medium">{selectedRecord.home_address_line2}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">City:</span>
                      <span className="font-medium">{selectedRecord.home_city}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">State:</span>
                      <span className="font-medium">{selectedRecord.home_state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Postal Code:</span>
                      <span className="font-medium">{selectedRecord.home_postal_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Country:</span>
                      <span className="font-medium">{selectedRecord.country}</span>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Status Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Publish Status:</span>
                      <span className={`font-medium px-2 py-0.5 rounded ${
                        selectedRecord.publish_status === 'PUBLISHED' ? 'bg-green-100 text-green-800' :
                        selectedRecord.publish_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        selectedRecord.publish_status === 'FAILED' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{selectedRecord.publish_status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Publish Attempts:</span>
                      <span className="font-medium">{selectedRecord.publish_attempts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Source File:</span>
                      <span className="font-medium text-xs break-all">{selectedRecord.source_file_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created:</span>
                      <span className="font-medium">{formatDateTime(selectedRecord.create_ts)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Updated:</span>
                      <span className="font-medium">{formatDateTime(selectedRecord.update_ts)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedRecord(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityData;
